import { useEffect, useRef, useState } from 'react'
import { type EditorCommand } from '../../utils/editorCommands.logic'

type EditorContextMenuProps = {
  x: number
  y: number
  commands: EditorCommand[]
  onClose: () => void
  onExecute: (id: EditorCommand['id']) => void
}

export function EditorContextMenu({ x, y, commands, onClose, onExecute }: EditorContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const enabledCommands = commands.filter(command => command.enabled)

  useEffect(() => {
    menuRef.current?.focus()
    const closeOnPointerDown = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) onClose()
    }
    document.addEventListener('pointerdown', closeOnPointerDown)
    return () => document.removeEventListener('pointerdown', closeOnPointerDown)
  }, [onClose])

  const executeActive = () => {
    const command = enabledCommands[activeIndex]
    if (!command) return
    onExecute(command.id)
    onClose()
  }

  return (
    <div
      ref={menuRef}
      className="editor-context-menu"
      role="menu"
      aria-label="Действия с планом"
      tabIndex={-1}
      style={{ left: Math.max(8, Math.min(x, window.innerWidth - 272)), top: Math.max(8, Math.min(y, window.innerHeight - 360)) }}
      onKeyDown={event => {
        if (event.key === 'Escape') { event.preventDefault(); onClose() }
        if (event.key === 'ArrowDown') { event.preventDefault(); setActiveIndex(index => Math.min(index + 1, enabledCommands.length - 1)) }
        if (event.key === 'ArrowUp') { event.preventDefault(); setActiveIndex(index => Math.max(index - 1, 0)) }
        if (event.key === 'Enter') { event.preventDefault(); executeActive() }
      }}
    >
      {commands.map(command => {
        const enabledIndex = enabledCommands.indexOf(command)
        const active = enabledIndex === activeIndex && command.enabled
        return (
          <button
            type="button"
            role="menuitem"
            disabled={!command.enabled}
            aria-disabled={!command.enabled}
            className={`editor-context-menu-item${active ? ' is-active' : ''}${command.dangerous ? ' is-dangerous' : ''}`}
            key={command.id}
            onMouseEnter={() => { if (command.enabled) setActiveIndex(enabledIndex) }}
            onClick={() => { if (command.enabled) { onExecute(command.id); onClose() } }}
          >
            <span>{command.label}</span>
            {command.shortcut && <kbd>{command.shortcut}</kbd>}
          </button>
        )
      })}
      {commands.length === 0 && <span className="editor-context-menu-empty">Нет доступных действий</span>}
    </div>
  )
}
